import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as api from "../../api/index";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import {
  FiShoppingBag,
  FiCreditCard,
  FiZap,
  FiTruck,
  FiInfo,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

const lgaDeliveryFees = {
  Ojo: 0,
  Agege: 0,
  "Ajeromi-Ifelodun": 0,
  Alimosho: 0,
  "Amuwo-Odofin": 0,
  Apapa: 0,
  Badagry: 0,
  Epe: 0,
  "Eti-Osa": 0,
  "Ibeju-Lekki": 0,
  "Ifako-Ijaiye": 0,
  Ikeja: 0,
  Ikorodu: 0,
  Kosofe: 0,
  "Lagos Island": 0,
  "Lagos Mainland": 0,
  Mushin: 0,
  "Oshodi-Isolo": 0,
  Shomolu: 0,
  Surulere: 0,
};

const getDeliveryFee = (lga) => lgaDeliveryFees[lga] || 1500;

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingKora, setLoadingKora] = useState(false);
  const [loadingXara, setLoadingXara] = useState(false);
  const token = localStorage.getItem("token");
  let decoded;
  try {
    decoded = jwtDecode(token);
  } catch (err) {
    decoded = {};
  }
  const userId = decoded?.id || decoded?._id;

  const [bonus, setBonus] = useState(0);
  const [amount, setAmount] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);

  const order = location.state?.order;
  const [includeDelivery, setIncludeDelivery] = useState(true);
  const userLGA = order?.lga;
  const deliveryFee = getDeliveryFee(userLGA);

  const subtotal = order ? Number(order.amount) * Number(order.quantity) : 0;
  const serviceFee = 0;
  const total = order
    ? subtotal + serviceFee + (includeDelivery ? deliveryFee : 0)
    : 0;

  useEffect(() => {
    if (!order || !order.sellerId) navigate(-1);
  }, [order, navigate]);

  console.log(order);

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        if (!userId) throw new Error("No user found");
        const res = await api.getUserBalance(userId);
        const fetchedBonus = Number(res.data?.bonus) || 0;
        const fetchedAmount = Number(res.data?.amount) || 0;
        setBonus(fetchedBonus);
        setAmount(fetchedAmount);
        setTotalBalance(fetchedAmount + fetchedBonus);
      } catch {
        /* ignore silently */
      }
    };
    fetchBalance();
  }, [userId]);

  const handlePayWithBalance = async (e) => {
    e.preventDefault();
    if (!order) return;
    if (total > totalBalance) {
      setMessage("❌ You don’t have enough balance to pay for this order.");
      return;
    }

    try {
      setLoading(true);
      setMessage("");
      const orderData = {
        ...order,
        userId,
        amount: total,
        quantity: Number(order.quantity),
        deliveryFee: includeDelivery ? deliveryFee : 0,
        serviceFee,
        lga: userLGA,
      };
      await api.Createorderbalance(orderData);
      setMessage("✅ Order created successfully with Balance!");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      setMessage("❌ Something went wrong while paying with balance.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayWithBank = async (e) => {
    e.preventDefault();
    if (!order) return;
    try {
      setLoadingKora(true);
      setMessage("");
      const orderData = {
        ...order,
        userId,
        amount: total,
        quantity: Number(order.quantity),
        deliveryFee: includeDelivery ? deliveryFee : 0,
        serviceFee,
        lga: userLGA,
      };
      const res = await api.Createorder(orderData);
      if (res.data?.checkout_url) {
        window.open(res.data.checkout_url, "_blank");
      } else {
        setMessage("❌ No checkout URL returned from server.");
      }
    } catch (err) {
      setMessage("❌ Something went wrong while paying with bank transfer.");
    } finally {
      setLoadingKora(false);
    }
  };

  const handlePayWithXara = async (e) => {
    e.preventDefault();

    if (!order) return;

    try {
      setLoadingXara(true);
      setMessage("");

      const xaraData = {
        ...order,
        userId,
        amount: total,
        quantity: Number(order.quantity),
        deliveryFee: includeDelivery ? deliveryFee : 0,
        serviceFee,
        lga: userLGA,
      };

      console.log("XARA ORDER DATA:", xaraData);

      const res = await api.XaraPayment(xaraData);

      console.log("XARA RESPONSE:", res.data);

      if (res.data?.success) {
        setMessage(
          `✅ Xara payment request created. Reference: ${res.data.reference}`,
        );
      } else {
        setMessage(res.data?.message || "❌ Unable to create Xara payment.");
      }
    } catch (err) {
      console.error("XARA PAYMENT ERROR:", err.response?.data || err);

      setMessage(
        err.response?.data?.message ||
          "❌ Something went wrong while creating Xara payment.",
      );
    } finally {
      setLoadingXara(false);
    }
  };

  if (!order) return null;

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">
            Back
          </span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight pr-10">
          Review & Pay
        </h1>
        <div />
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        {/* 🛒 Order Summary Card */}
        <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-50 pb-4">
            <div className="w-10 h-10 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center">
              <FiShoppingBag size={20} />
            </div>
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">
              Order Summary
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-xs font-bold text-gray-400 uppercase">
                Item
              </span>
              <span className="text-sm font-black text-gray-900">
                {order.item}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs font-bold text-gray-400 uppercase">
                Qty & Price
              </span>
              <span className="text-sm font-black text-gray-900">
                {order.quantity} x ₦{Number(order.amount).toLocaleString()}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 font-medium italic">
              "{order.description}"
            </p>
          </div>
        </div>

        {/* 💳 Payment Breakdown Card */}
        <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-50 pb-4">
            <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center">
              <FiInfo size={20} />
            </div>
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">
              Pricing
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between text-sm font-bold text-gray-600">
              <span>Subtotal</span>
              <span>₦{subtotal.toLocaleString()}</span>
            </div>

            {/* Delivery Toggle Area */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="deliveryFee"
                  checked={includeDelivery}
                  onChange={() => setIncludeDelivery(!includeDelivery)}
                  className="w-5 h-5 accent-orange-600 rounded-lg cursor-pointer"
                />
                <label
                  htmlFor="deliveryFee"
                  className="flex flex-col cursor-pointer"
                >
                  <span className="text-xs font-black text-gray-900 uppercase tracking-tighter">
                    Delivery Fee ({userLGA})
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase">
                    Doorstep Pickup & Drop
                  </span>
                </label>
              </div>
              <span
                className={`text-sm font-black ${includeDelivery ? "text-gray-900" : "text-gray-300 line-through"}`}
              >
                ₦{deliveryFee.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between text-sm font-bold text-gray-600">
              <span>Service Fee</span>
              <span>₦{serviceFee.toLocaleString()}</span>
            </div>
            <div className="pt-4 border-t border-dashed flex justify-between items-center">
              <span className="text-xs font-black text-gray-400 uppercase">
                Total Payable
              </span>
              <span className="text-2xl font-black text-orange-600">
                ₦{total.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* 💰 Wallet Status */}
        <div className="bg-gray-900 p-6 rounded-[2.5rem] shadow-xl mb-8 relative overflow-hidden">
          <div className="absolute right-[-5%] bottom-[-10%] w-24 h-24 bg-white/5 rounded-full blur-2xl"></div>
          <div className="relative z-10 flex justify-between items-center">
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                Your Wallet
              </p>
              <h4 className="text-lg font-black text-white">
                ₦{totalBalance.toLocaleString()}
              </h4>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-black text-orange-400 uppercase">
                Bonus: ₦{bonus.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* 🚀 Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handlePayWithBalance}
            disabled={loading}
            className={`w-full py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-3 shadow-lg active:scale-95 ${
              loading
                ? "bg-gray-200 text-gray-400"
                : "bg-orange-600 text-white shadow-orange-200"
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
            ) : (
              <>
                <FiZap /> Pay with Wallet
              </>
            )}
          </button>

          <button
            onClick={handlePayWithBank}
            disabled={loadingKora}
            className={`w-full py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-3 border-2 active:scale-95 ${
              loadingKora
                ? "bg-gray-100 text-gray-400 border-gray-100"
                : "border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
            }`}
          >
            {loadingKora ? (
              <div className="w-5 h-5 border-2 border-gray-400 border-t-gray-900 rounded-full animate-spin"></div>
            ) : (
              <>
                <FiCreditCard /> Pay with Paystack
              </>
            )}
          </button>
          <button
            onClick={handlePayWithXara}
            disabled={loadingXara || loadingKora || loading}
            className={`w-full py-5 rounded-[1.5rem] font-black uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-3 border-2 active:scale-95 ${
              loadingXara
                ? "bg-gray-100 text-gray-400 border-gray-100"
                : "border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white"
            }`}
          >
            {loadingXara ? (
              <div className="w-5 h-5 border-2 border-gray-400 border-t-orange-600 rounded-full animate-spin"></div>
            ) : (
              <>
                <FiCreditCard />
                Pay with Xara
              </>
            )}
          </button>
        </div>

        {/* Status Message */}
        {message && (
          <div
            className={`mt-6 p-4 rounded-2xl flex items-center gap-3 ${message.startsWith("✅") ? "bg-green-50 text-green-700 border border-green-100" : "bg-red-50 text-red-700 border border-red-100"}`}
          >
            {message.startsWith("✅") ? <FiCheckCircle /> : <FiAlertCircle />}
            <p className="text-xs font-black uppercase tracking-tight">
              {message}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
